import '@/styles/reset.css';
import '@/styles/fonts.css';
import '@/styles/global.css';
import '@bumble/ui-kit/index.css';
import '@/styles/ui-kit-overrides.css';
import 'lenis/dist/lenis.css';
import { useEffect } from 'react';

import Footer from '@/components/footer';
import Header from '@/components/header';
import TypeformModal from '@/components/typeform-modal';
import { Localization } from '@bumble/localization/components';
import Lenis from 'lenis';
import { AppError } from 'lib/app-error';
import { GlobalProps } from 'lib/app/global-props';
import { GlobalPropsProvider } from 'lib/app/global-props-provider';
import { listenForConsentEvents, listenForPrivacyManagerActions } from 'lib/consent-mode';
import { Country } from 'lib/enums/country';
import { Provider as LocaleProvider } from 'lib/locale-context';
import localization from 'lib/localization';
import { SupportedLanguage, supportedLanguages } from 'lib/supported-languages';
import { useAmp } from 'next/amp';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';

type AppPageProps = {
  lang: SupportedLanguage;
};

const defaultGlobalProps: GlobalProps = { country: Country.UNKNOWN, lang: 'en' };

type AppWithGlobalProps = AppProps<AppPageProps> & {
  // Set by _document during SSR; on the client it is read from window.appProps
  globalProps?: GlobalProps;
};

function App({ Component, pageProps, globalProps }: AppWithGlobalProps) {
  const router = useRouter();

  const lang = router.locale || getDefaultLanguage(pageProps.lang);
  const lexemes = getLang(
    // @ts-ignore - string conversion to lang
    router.locale?.indexOf('_') !== -1 ? router.locale?.split('_')[0] : router.locale,
  );

  const isAmp = useAmp();

  const globalPropsValue =
    globalProps ??
    (typeof window !== 'undefined'
      ? (window as unknown as { appProps?: GlobalProps }).appProps
      : undefined);

  // Bridge Sourcepoint consent (GDPR/TCF events, and CCPA privacy manager actions) to
  // Google Consent Mode (JS-30759, JS-30760). Production only, matching where the CMP
  // and GTM scripts are injected in pages/_document.tsx.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      return;
    }

    const stopConsentEvents = listenForConsentEvents();
    const stopPrivacyManagerActions = listenForPrivacyManagerActions();

    return () => {
      stopConsentEvents();
      stopPrivacyManagerActions();
    };
  }, []);

  useEffect(() => {
    const lenis = new Lenis();

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  return (
    <LocaleProvider value={lang}>
      <GlobalPropsProvider value={globalPropsValue ?? defaultGlobalProps}>
        <Localization
          // @ts-ignore – Localization type mismatch between /dist/es and /dist paths due to dual module resolution
          instance={localization}
          // @ts-ignore - Type '"es-cl"' is not assignable to type 'FetchedLanguageId | undefined'. Did you mean '"es-co"'?ts(2322)
          lang={router.locale?.indexOf('_') !== -1 ? router.locale?.split('_')[0] : router.locale}
          entries={lexemes.lexemes}
          commonWords={lexemes.common}
        >
          <Head>
            {isAmp ? null : (
              <meta
                name="viewport"
                content="width=device-width, initial-scale=1, viewport-fit=cover"
              />
            )}
          </Head>

          <div>
            {/* @ts-ignore – AppPageProps is narrowed but runtime pageProps contains data and locales */}
            <Header data={pageProps?.data?.header} locales={pageProps?.data?.site?.locales} />
            <div className="page" id={`page-${router.asPath.replaceAll('/', '-')}`}>
              <Component {...pageProps} />
            </div>
            {/* @ts-ignore – AppPageProps is narrowed but runtime pageProps contains data and locales */}
            <Footer locales={pageProps?.data?.site?.locales} />
            <TypeformModal />
          </div>
        </Localization>
      </GlobalPropsProvider>
    </LocaleProvider>
  );
}

export default App;

function getDefaultLanguage(lang: string): SupportedLanguage {
  if (supportedLanguages.includes(lang as SupportedLanguage)) {
    return lang as SupportedLanguage;
  } else if (typeof lang === 'string') {
    // We have a language being passed, but it's not valid, it means user is on an invalid url
    // e.g. /xx/the-buzz or team.bumble.com/xx/open-roles
    throw new AppError({
      statusCode: 404,
      message: `Invalid page language - ${lang}`,
    });
  }

  return 'en';
}

function getLang(lang: SupportedLanguage) {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  return require(`lib/lexemes/${lang}`).default;
}
